import { useCallback } from 'react';

import { graphql, useMutation } from 'react-relay';
import type { PayloadError } from 'relay-runtime';

import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import type {
	useAssignAgentMutation as useAssignAgentMutationType,
	useAssignAgentMutation$data,
} from './__generated__/useAssignAgentMutation.graphql';

type AssignAgentArgs = {
	cloudId: string;
	issueId: string;
	agentId: string | null;
	identityAccountId: string;
};

export class AssignAgentError extends Error {
	statusCode: number | undefined;
	constructor(message: string, statusCode?: number) {
		super(message);
		this.name = 'AssignAgentError';
		this.statusCode = statusCode;
	}
}

type UseAssignAgentOptions = {
	conversationId: string | undefined;
	invocationId: string | undefined;
	messageId: string | undefined;
};

const MAX_ATTEMPTS = 7;
const INITIAL_RETRY_DELAY_MS = 1000;
const RETRY_BACKOFF_FACTOR = 2;

const buildAssigneeFieldAri = (cloudId: string, issueId: string): string =>
	`ari:cloud:jira:${cloudId}:issuefieldvalue/${issueId}/assignee`;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const useAssignAgent = ({
	conversationId,
	invocationId,
	messageId,
}: UseAssignAgentOptions): {
	assignAgent: (args: AssignAgentArgs) => Promise<useAssignAgentMutation$data>;
	isAssigning: boolean;
} => {
	const { sendAnalyticsEvent } = useAnalytics();

	const [commit, isAssigning] = useMutation<useAssignAgentMutationType>(graphql`
		mutation useAssignAgentMutation($input: JiraUpdateSingleSelectUserPickerFieldInput!) {
			jira {
				updateSingleSelectUserPickerField(input: $input) {
					success
					errors {
						message
						extensions {
							statusCode
						}
					}
					field {
						... on JiraSingleSelectUserPickerField {
							id
							user {
								accountId
							}
						}
					}
				}
			}
		}
	`);

	const assignAgent = useCallback(
		async ({
			cloudId,
			issueId,
			identityAccountId,
		}: AssignAgentArgs): Promise<useAssignAgentMutation$data> => {
			const fieldAri = buildAssigneeFieldAri(cloudId, issueId);

			const trackFailure = () => {
				sendAnalyticsEvent({
					eventType: 'track',
					actionSubject: 'assignAgent',
					action: 'failed',
					attributes: {
						analyticsSource: 'inline-create-agent-card',
						conversationId,
						invocationId,
						messageId,
						issueId,
					},
				});
			};

			// After agent creation, Identity may not have synced the new bot account
			// into Jira's user store yet. Retry with exponential backoff to handle the
			// race. Only transient errors (400 user-not-found) are retried — other
			// errors (e.g. 403 Forbidden) bail out immediately so we don't block the user.
			const attemptOnce = (): Promise<{ data: useAssignAgentMutation$data; retryable: boolean }> =>
				new Promise((resolve, reject) => {
					commit({
						variables: {
							input: {
								id: fieldAri,
								operation: {
									id: `ari:cloud:identity::user/${identityAccountId}`,
									operation: 'SET',
								},
							},
						},
						onCompleted: (response, errors: PayloadError[] | null | undefined) => {
							if (errors && errors.length > 0) {
								reject({ message: errors.map((e) => e.message).join(', '), retryable: false });
								return;
							}
							const mutationResult = response.jira?.updateSingleSelectUserPickerField;
							if (!mutationResult?.success) {
								const statusCode = mutationResult?.errors?.[0]?.extensions?.statusCode;
								const message = mutationResult?.errors?.[0]?.message ?? 'Failed to assign agent';
								// 400 = user not yet synced to Jira's directory — transient, retry.
								// Any other code (403 forbidden, 500, etc.) = permanent, bail immediately.
								const retryable = statusCode === 400;
								reject({ message, retryable, statusCode });
								return;
							}
							sendAnalyticsEvent({
								eventType: 'track',
								actionSubject: 'issue',
								action: 'updated',
								actionSubjectId: 'ended',
								attributes: {
									analyticsSource: 'inline-create-agent-card',
									conversationId,
									invocationId,
									messageId,
									issueId,
									fieldType: 'singleUserPicker',
									newValId: identityAccountId,
									actionTaken: 'add',
									fieldKey: 'assignee',
									is_new_1event: true,
								},
							});
							resolve({ data: response, retryable: false });
						},
						onError: (error) => reject({ message: String(error), retryable: false }),
					});
				});

			let lastError: { message: string; retryable: boolean; statusCode?: number } | undefined;
			let delayMs = INITIAL_RETRY_DELAY_MS;
			for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
				try {
					const { data } = await attemptOnce();
					return data;
				} catch (err) {
					lastError = err as { message: string; retryable: boolean; statusCode?: number };
					if (!lastError.retryable || attempt >= MAX_ATTEMPTS) {
						break;
					}
					await sleep(delayMs);
					delayMs *= RETRY_BACKOFF_FACTOR;
				}
			}
			trackFailure();
			throw new AssignAgentError(
				lastError?.message ?? 'Failed to assign agent',
				lastError?.statusCode,
			);
		},
		[commit, sendAnalyticsEvent, conversationId, invocationId, messageId],
	);

	return { assignAgent, isAssigning };
};
